/**
 * Services by Lifecycle Widget (issue #1652, part of epic #1650)
 *
 * Shows the distribution of the current user's division-scoped services
 * across lifecycle stages, matching `homepage-dashboard-spec.md`'s
 * "Services by Lifecycle Status" widget. Division scoping is resolved
 * server-side by walking `parentTeamId` to the hierarchy root (ADR-1254),
 * never a `Team.division` field. Follows the single-query +
 * `useStatisticsWidget` pattern shared by `DependencyHealthWidget`.
 */

import { useServicesByLifecycle } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import { StatusBadge } from '../shared/StatusBadge';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './ServicesByLifecycleWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'services-by-lifecycle',
  title: 'Services by Lifecycle',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling division-scoped widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface ServicesByLifecycleStats {
  data: Array<{ status: string; count: number; percentage: number }>;
  total: number;
  scope: string;
}

type ServicesByLifecycleQueryData = ReturnType<typeof useServicesByLifecycle>['data'];

function extractServicesByLifecycleStats(
  data: ServicesByLifecycleQueryData
): ServicesByLifecycleStats | null {
  if (!data?.servicesByLifecycle) {
    return null;
  }
  return data.servicesByLifecycle;
}

export function ServicesByLifecycleWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useServicesByLifecycle(),
    extractServicesByLifecycleStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="services-by-lifecycle-widget__empty">
          <IconElement name="chart_arc" className="services-by-lifecycle-widget__empty-icon" />
          <p className="forge-typography--body2">No lifecycle data available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="services-by-lifecycle-widget__content">
        <p
          className="services-by-lifecycle-widget__scope-label forge-typography--caption"
          data-testid="services-by-lifecycle-scope"
        >
          Scope: {stats.scope}
        </p>

        <div className="services-by-lifecycle-widget__total">
          <div className="services-by-lifecycle-widget__total-number">{stats.total}</div>
          <div className="services-by-lifecycle-widget__total-label forge-typography--label2">
            Total Services
          </div>
        </div>

        {stats.data.length === 0 ? (
          <p className="forge-typography--body2">No services in this scope</p>
        ) : (
          <ul
            className="services-by-lifecycle-widget__list"
            data-testid="services-by-lifecycle-list"
          >
            {stats.data.map((entry) => (
              <li key={entry.status} className="services-by-lifecycle-widget__row">
                <StatusBadge variant="lifecycle" value={entry.status} />
                <div className="services-by-lifecycle-widget__bar-track">
                  <div
                    className="services-by-lifecycle-widget__bar-fill"
                    style={{ width: `${entry.percentage}%` }}
                  />
                </div>
                <span className="services-by-lifecycle-widget__count forge-typography--label1">
                  {entry.count} ({entry.percentage}%)
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardWidget>
  );
}
